import { useCallback } from "react";
import { Button, Dropdown } from "react-bootstrap";
import { GridCellProps } from "@progress/kendo-react-grid";
import { MenuItem } from "@progress/kendo-react-layout";
import { ChargeCalculation, LocationModel, ServiceModel } from "../../data/entities";
import { PermissionDefinitions } from "../../security";
import { ServiceLocationService, ServiceService } from "../../services";
import { usePageActions } from "../";
import { useUrlParams,
  useSharedState,
  useGridActions,
  useCRUDHandlers,
  usePermission,
  useSetPageTitle,
  useTenantFeatures} from "../../hooks";
import { AddAllServiceModal, AddServiceModal, ManageServiceModal } from "./components";
import { IconAndText,
  IconDefinitions,
  Icon,
  TitlelessCard,
  ODataGrid,
  ResponsiveGridColumn,
  Breakpoints,
  ConfirmationModal } from "../../components";
import { createODataQueryFilter, formatPrice, showErrorMessage, showSuccessMessage } from "../../utils";
import { getUserInfo, getUserTenantId, isLocationAdmin } from "../../app";
import { useSelector } from "react-redux";


export function ServiceTypeServicesPage() {

  const serviceTypeId = parseInt(useUrlParams("id"));

  const serviceService = new ServiceService();
  const serviceLocation = new ServiceLocationService(); 

  const gridData = useSharedState<Array<ServiceModel>>([]);
  const { onClick, onSelect, showAddState, showEditState, showDeleteState, editItemState, deleteItemState, showDelete } = useGridActions<ServiceModel>();
  const { showAddState: showAddStateLoc } = useGridActions<LocationModel>();
  const { gridRef, getItems, addItem, editItem, deleteItem } = useCRUDHandlers<ServiceModel>(serviceService, "Service", showAddState, showEditState);
  const tenantId = useSelector(getUserTenantId());

  const { manageServiceTypes, manageLocationServices } = useTenantFeatures();
  const canAddServicePermission = usePermission(PermissionDefinitions.service.create(tenantId!));
  const canAddService = canAddServicePermission && manageServiceTypes;
  const canAddAllServicesToLocation = canAddServicePermission && manageLocationServices;
  const canEditServicePermission = usePermission(PermissionDefinitions.service.edit());
  const canDeleteService = usePermission(PermissionDefinitions.service.delete()) && manageServiceTypes;
  const locationAdmin = useSelector(isLocationAdmin())
   
  useSetPageTitle("Services", []);

  usePageActions(
    (canAddService || canAddAllServicesToLocation) &&
     (
      <Dropdown>
        <Dropdown.Toggle as="div" className="cursor-pointer">
          <Button variant="primary">
            <IconAndText iconName={IconDefinitions.add} text="Add" />
          </Button>
        </Dropdown.Toggle>
        <Dropdown.Menu className="text-center border-radius-sm">
          {canAddService && (
            <Dropdown.Item
              onClick={() => {
                showAddState.setter(true);
              }}
            >
              New Service
            </Dropdown.Item>
          )}
          {canAddAllServicesToLocation && (
            <Dropdown.Item
              onClick={() => {
                showAddStateLoc.setter(true);
              }}
            >
               All Services to Location
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>
    ),[canAddService, canAddAllServicesToLocation]
  );
  const commandsCell = useCallback(
    (props: GridCellProps) => (
      <td className="text-center">
        <Icon
          iconName={IconDefinitions.delete}
          className="text-danger"
          onClick={() => showDelete(props.dataItem)}
        />
      </td>
    ),
    [showDelete]
  );

  const handleAddAllService = async (event: any)=>{
    try {
      const ok = await serviceLocation.addServiceToLocation(event.locationId, serviceTypeId); ;
      if (ok) {
        showSuccessMessage("services added successfully.");
        showAddStateLoc?.setter(false);
      }
    } catch (error) {
      showErrorMessage(`Error occurred while trying to execute insert: ${error}`);
    } finally {
      showAddStateLoc?.setter(false);
      gridRef.current?.refreshData();
    }
  }

  return (
    <TitlelessCard bodyClassName="p-0">
      <ODataGrid
        ref={gridRef}
        getData={(oDataQuery) => {
          const filterQuery = createODataQueryFilter(oDataQuery,[`serviceTypeId eq ${serviceTypeId}`])
          return getItems(filterQuery)
        }}
        dataState={gridData}
        onRowMenuSelect={onSelect}
        onRowClick={((canEditServicePermission || locationAdmin) && (manageServiceTypes || manageLocationServices)) === true ? onClick : ()=> {return}}
        sort={[{ field: "name", dir: "asc"}]}
        scrollable="scrollable"
        pageSize={10}
      >
        <ResponsiveGridColumn
          title="Name"
          field="name"
          sortable={true}
          width={'auto'}
        />
        <ResponsiveGridColumn
          title="Default Price"
          field="defaultPrice"
          sortable={true}
          filterable={false}
          width={'auto'}
          cell={(props) => <td>{formatPrice(props.dataItem.defaultPrice)}</td>}
          breakpoint={Breakpoints.sm}
        />
        <ResponsiveGridColumn
          title="Charge Type"
          field="defaultChargeType"
          sortable={true}
          width={'auto'}
          filterable={false}
          breakpoint={Breakpoints.md}
        />
         <ResponsiveGridColumn
          title="Charge Calculation"
          field="defaultChargeCalculation"
          sortable={true}
          filterable={false}
          width={'auto'}
          breakpoint={Breakpoints.lg}
        />
         <ResponsiveGridColumn
          title="Charge Amount"
          field="defaultChargeAmount"
          sortable={true}
          filterable={false}
          width={'auto'}
          cell={(props) => props.dataItem.defaultChargeCalculation === ChargeCalculation.Fixed ? <td>{formatPrice(props.dataItem.defaultChargeAmount)}</td> : <td>{props.dataItem.defaultChargeAmount}%</td>}
          breakpoint={Breakpoints.xl}
       />
        <ResponsiveGridColumn
          title="Hide Providers"
          field="hideProviders"
          filterable={false}
          width={'auto'}
          cell={(props) => <td>{props.dataItem.hideProviders ? "Yes" : "No"}</td>}
          breakpoint={Breakpoints.xxl}
        />
        <ResponsiveGridColumn
          title="Allow Any Providers"
          field="allowAnyProvider"
          filterable={false}
          width={'auto'}
          cell={(props) => <td>{props.dataItem.allowAnyProvider ? "Yes" : "No"}</td>}
          breakpoint={Breakpoints.xxl}
        />
        {canDeleteService === true && (
          <ResponsiveGridColumn
            className="column-url"
            field="url"
            title=" "
            sortable={false}
            filterable={false}
            headerCell={() => null}
            cell={commandsCell}
            width={50}
          />
        )}
        <MenuItem
          text="Open"
          data={{ action: "open" }}
          icon="hyperlink-open"
        />
        {canDeleteService && (
          <MenuItem
            text="Delete"
            data={{ action: "delete" }}
            icon={IconDefinitions.delete}
          />
        )}
      </ODataGrid>
      <AddServiceModal
        showAddState={showAddState}
        showState={showAddState}
        onSubmit={addItem}
      />
      <AddAllServiceModal
        showAddState={showAddStateLoc}
        showState={showAddStateLoc}
        onSubmit={handleAddAllService}
      />
      <ManageServiceModal
        showState={showEditState}
        dataItemState={editItemState}
        onSubmit={editItem}
      />
      <ConfirmationModal
        showState={showDeleteState}
        title={`Delete ${deleteItemState.value?.name}`}
        message={`You are about to delete this service, it cannot be undone. Are you sure you want to delete ${deleteItemState.value.name}?`}
        confirmText="Delete"
        onConfirm={() => deleteItem(deleteItemState.value)}
      />
    </TitlelessCard>
  );
}

export default ServiceTypeServicesPage;

